import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ENQUIRY_TYPES = [
  "Co-investment opportunity",
  "Deal introduction",
  "Capital raise / fundraising",
  "M&A / buy-side",
  "Strategic partnership",
  "Other",
];

const SECTORS = [
  "Fintech",
  "Healthcare",
  "Agriculture",
  "Real Estate Development",
  "Clean Energy",
  "Cross-sector",
];

const STAGES = [
  "Seed / Early",
  "Growth",
  "Late-stage / Pre-IPO",
  "Buyout / Control",
  "Development / Greenfield",
  "Mature / Yield",
];

const TICKETS = [
  "Under USD 5M",
  "USD 5M — 25M",
  "USD 25M — 100M",
  "USD 100M — 250M",
  "Over USD 250M",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  firm: "",
  role: "",
  linkedin: "",
  enquiry_type: "",
  sector: "",
  geography: "",
  stage: "",
  ticket_size: "",
  summary: "",
  confidentiality_ack: false,
};

export default function Opportunity() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: "" }));
  };

  const onChange = (e) => set(e.target.name, e.target.value);

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Required";
    if (!form.email.trim()) er.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      er.email = "Enter a valid email";
    if (!form.firm.trim()) er.firm = "Required";
    if (!form.enquiry_type) er.enquiry_type = "Select an option";
    if (!form.sector) er.sector = "Select a sector";
    if (!form.geography.trim()) er.geography = "Required";
    if (!form.stage) er.stage = "Select a stage";
    if (!form.ticket_size) er.ticket_size = "Select a ticket size";
    if (!form.summary.trim() || form.summary.trim().length < 20)
      er.summary = "Please provide at least 20 characters";
    if (!form.confidentiality_ack)
      er.confidentiality_ack = "You must acknowledge confidentiality";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length > 0) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        firm: form.firm.trim(),
        geography: form.geography.trim(),
        summary: form.summary.trim(),
        phone: form.phone.trim() || null,
        role: form.role.trim() || null,
        linkedin: form.linkedin.trim() || null,
      };
      const res = await axios.post(`${API}/opportunity`, payload);
      if (res.status === 200) {
        setSubmitted(true);
        setForm(initial);
        toast.success(
          "Opportunity received. A principal will respond personally within 5 business days."
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-opportunity">
      {/* HERO */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20 bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 overline text-emerald-400">
              <Lock size={14} /> Confidential Enquiry Portal
            </div>
            <h1
              data-testid="opportunity-title"
              className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.05]"
            >
              Submit an investment opportunity.
            </h1>
            <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-xl">
              For operators, intermediaries, family offices, and co-investors.
              Share the essentials of your opportunity. A principal will
              respond personally — discretion guaranteed.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-slate-800 bg-slate-900 p-8">
              <div className="flex items-center gap-3">
                <ShieldCheck size={20} className="text-emerald-400" />
                <div className="font-serif text-xl text-white">
                  How we treat your submission
                </div>
              </div>
              <ul className="mt-5 space-y-3 text-sm text-slate-300 leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">·</span>
                  <span>
                    Reviewed only by principals and the relevant sector partner.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">·</span>
                  <span>
                    Never shared outside the firm without your written consent.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">·</span>
                  <span>
                    Response within 5 business days — including a clear pass or
                    next step.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">·</span>
                  <span>NDA available on request before substantive review.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="py-16 lg:py-24 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          {submitted ? (
            <div
              data-testid="opportunity-success"
              className="border border-slate-200 bg-slate-50 p-10 lg:p-14 text-center"
            >
              <ShieldCheck
                size={40}
                className="mx-auto text-emerald-700"
                strokeWidth={1.4}
              />
              <h2 className="mt-6 font-serif text-3xl lg:text-4xl tracking-tight text-slate-900">
                Submission received.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed max-w-xl mx-auto">
                A principal will review your enquiry personally and respond
                within 5 business days. Thank you for considering Finovest.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="opportunity-submit-another"
                  className="text-[12px] tracking-[0.2em] uppercase font-semibold border border-slate-900 text-slate-900 px-6 py-3 hover:bg-slate-900 hover:text-white transition-colors"
                >
                  Submit another
                </button>
                <Link
                  to="/"
                  data-testid="opportunity-return-home"
                  className="text-[12px] tracking-[0.2em] uppercase font-semibold bg-slate-900 text-white px-6 py-3 hover:bg-slate-800 transition-colors"
                >
                  Return home
                </Link>
              </div>
            </div>
          ) : (
            <form
              data-testid="opportunity-form"
              onSubmit={onSubmit}
              noValidate
              className="space-y-10"
            >
              {/* Submitter */}
              <fieldset>
                <legend className="overline">Submitter</legend>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field
                    label="Full name *"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    error={errors.name}
                    testid="opp-input-name"
                  />
                  <Field
                    label="Email *"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    error={errors.email}
                    testid="opp-input-email"
                  />
                  <Field
                    label="Phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    testid="opp-input-phone"
                  />
                  <Field
                    label="LinkedIn profile"
                    name="linkedin"
                    value={form.linkedin}
                    onChange={onChange}
                    placeholder="https://linkedin.com/in/…"
                    testid="opp-input-linkedin"
                  />
                  <Field
                    label="Firm / Organization *"
                    name="firm"
                    value={form.firm}
                    onChange={onChange}
                    error={errors.firm}
                    testid="opp-input-firm"
                  />
                  <Field
                    label="Your role"
                    name="role"
                    value={form.role}
                    onChange={onChange}
                    testid="opp-input-role"
                    placeholder="e.g. Founder, MD, GP, CFO"
                  />
                </div>
              </fieldset>

              {/* Opportunity */}
              <fieldset>
                <legend className="overline">Opportunity</legend>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <SelectField
                    label="Type of enquiry *"
                    value={form.enquiry_type}
                    onChange={(v) => set("enquiry_type", v)}
                    options={ENQUIRY_TYPES}
                    error={errors.enquiry_type}
                    testid="opp-select-enquiry-type"
                    placeholder="Select…"
                  />
                  <SelectField
                    label="Sector *"
                    value={form.sector}
                    onChange={(v) => set("sector", v)}
                    options={SECTORS}
                    error={errors.sector}
                    testid="opp-select-sector"
                    placeholder="Select…"
                  />
                  <Field
                    label="Geography *"
                    name="geography"
                    value={form.geography}
                    onChange={onChange}
                    error={errors.geography}
                    testid="opp-input-geography"
                    placeholder="e.g. Singapore, Germany, South Africa"
                  />
                  <SelectField
                    label="Stage *"
                    value={form.stage}
                    onChange={(v) => set("stage", v)}
                    options={STAGES}
                    error={errors.stage}
                    testid="opp-select-stage"
                    placeholder="Select…"
                  />
                  <SelectField
                    label="Ticket size *"
                    value={form.ticket_size}
                    onChange={(v) => set("ticket_size", v)}
                    options={TICKETS}
                    error={errors.ticket_size}
                    testid="opp-select-ticket"
                    placeholder="Select…"
                  />
                </div>

                <div className="mt-6">
                  <Label
                    htmlFor="summary"
                    className="overline text-slate-500 mb-2 block"
                  >
                    Opportunity summary *
                  </Label>
                  <Textarea
                    id="summary"
                    name="summary"
                    rows={6}
                    value={form.summary}
                    onChange={onChange}
                    data-testid="opp-input-summary"
                    placeholder="Briefly describe the opportunity — business, traction, capital sought, timing, and what you would value most from Finovest."
                    className={`bg-white border-slate-200 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 rounded-none ${
                      errors.summary ? "border-rose-400" : ""
                    }`}
                  />
                  {errors.summary && (
                    <p
                      data-testid="opp-error-summary"
                      className="mt-2 text-xs text-rose-600"
                    >
                      {errors.summary}
                    </p>
                  )}
                </div>
              </fieldset>

              {/* Confidentiality */}
              <div className="border border-slate-200 bg-slate-50 p-5 flex items-start gap-3">
                <Checkbox
                  id="confidentiality_ack"
                  checked={form.confidentiality_ack}
                  onCheckedChange={(v) => set("confidentiality_ack", !!v)}
                  data-testid="opp-check-confidentiality"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label
                    htmlFor="confidentiality_ack"
                    className="text-sm text-slate-700 leading-relaxed cursor-pointer"
                  >
                    I acknowledge that my submission is shared with Finovest
                    Holdings Ltd in confidence, and consent to internal review
                    by the firm&apos;s principals and relevant sector partner.
                    Finovest will not disclose the contents externally without
                    my prior written consent. *
                  </Label>
                  {errors.confidentiality_ack && (
                    <p
                      data-testid="opp-error-confidentiality"
                      className="mt-2 text-xs text-rose-600"
                    >
                      {errors.confidentiality_ack}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="opportunity-submit"
                  className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase font-semibold bg-slate-900 text-white px-8 py-4 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Submitting
                    </>
                  ) : (
                    <>
                      Submit opportunity <ArrowRight size={14} />
                    </>
                  )}
                </button>
                <Link
                  to="/contact"
                  data-testid="opportunity-general-contact"
                  className="text-[12px] tracking-[0.2em] uppercase font-semibold text-slate-600 underline-offset-4 hover:underline hover:text-slate-900 transition-colors"
                >
                  General enquiry instead →
                </Link>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  value,
  onChange,
  error,
  testid,
  placeholder,
}) {
  return (
    <div>
      <Label
        htmlFor={name}
        className="overline text-slate-500 mb-2 block"
      >
        {label}
      </Label>
      <Input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        placeholder={placeholder}
        className={`bg-white border-slate-200 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 rounded-none h-12 ${
          error ? "border-rose-400" : ""
        }`}
      />
      {error && (
        <p
          data-testid={`${testid}-error`}
          className="mt-2 text-xs text-rose-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  error,
  testid,
  placeholder,
}) {
  return (
    <div>
      <Label className="overline text-slate-500 mb-2 block">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testid}
          className={`bg-white border-slate-200 rounded-none h-12 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 ${
            error ? "border-rose-400" : ""
          }`}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="rounded-none">
          {options.map((o) => (
            <SelectItem
              key={o}
              value={o}
              data-testid={`${testid}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && (
        <p
          data-testid={`${testid}-error`}
          className="mt-2 text-xs text-rose-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}
